/**
 * Idle sign-out (FR-ORG-03): after N minutes without input (default 30, Admin setting
 * from /settings/public), warn for the last 60 seconds, then sign out. Activity in any tab of the
 * app counts (shared through localStorage).
 */
import { useEffect, useRef, useState } from 'react';
import { Button, Dialog } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { usePublicSettings } from '../../lib/queries';

const KEY = 'dms.lastActive';
const WARN_MS = 60_000;

export function IdleGuard() {
  const { user, signOut } = useAuth();
  const settings = usePublicSettings();
  const minutes = settings.data?.idleTimeoutMinutes ?? 30;
  const limit = minutes * 60_000;
  const last = useRef(Date.now());
  const [left, setLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!user) return;
    let lastWrite = 0;
    const touch = () => {
      const now = Date.now();
      last.current = now;
      if (now - lastWrite > 5000) {
        lastWrite = now;
        try {
          localStorage.setItem(KEY, String(now));
        } catch {
          /* ignore */
        }
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY && e.newValue) last.current = Math.max(last.current, Number(e.newValue));
    };
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
    events.forEach((ev) => window.addEventListener(ev, touch, { passive: true }));
    window.addEventListener('storage', onStorage);
    touch();
    const timer = window.setInterval(() => {
      const idle = Date.now() - last.current;
      if (idle >= limit) {
        window.clearInterval(timer);
        void signOut('idle');
      } else if (idle >= limit - WARN_MS) setLeft(Math.ceil((limit - idle) / 1000));
      else setLeft(null);
    }, 1000);
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, touch));
      window.removeEventListener('storage', onStorage);
      window.clearInterval(timer);
    };
  }, [user, limit, signOut]);

  return (
    <Dialog
      open={left !== null}
      size="sm"
      title="Still there?"
      onClose={() => {
        last.current = Date.now();
        setLeft(null);
      }}
      footer={
        <>
          <Button variant="secondary" onClick={() => void signOut()}>
            Sign out now
          </Button>
          <Button
            onClick={() => {
              last.current = Date.now();
              setLeft(null);
            }}
          >
            Stay signed in
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted">
        For security, you will be signed out in <span className="tabular font-semibold text-ink">{left ?? 0} seconds</span> because nothing has happened here for {minutes} minutes.
      </p>
    </Dialog>
  );
}
